/**
 * Supported locales (TMMAPP-0001). Kept in step with saas-auth's supported set (en/es/fr/pt).
 * The product launches in one language; every user-visible string still goes through i18next.
 */
export const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'pt'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
/**
 * The BCP-47 tag a locale renders under: `<html lang>` and every Intl formatter (TMM-L1 — pt is
 * Brazilian Portuguese, never ICU's default for bare `pt`). The stored preference and the catalogs
 * keep the bare `pt` key; en, es and fr stay bare.
 */
export type LanguageTag = 'en' | 'es' | 'fr' | 'pt-BR';
const LANGUAGE_TAGS: Record<SupportedLocale, LanguageTag> = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  pt: 'pt-BR',
};
export function languageTag(locale: SupportedLocale): LanguageTag {
  return LANGUAGE_TAGS[locale];
}

export const DEFAULT_LOCALE: SupportedLocale = 'en';
export const LOCALE_STORAGE_KEY = 'tmm_locale';

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** `es-MX` → `es`; unsupported or empty → null. */
export function normalizeLocale(input: string | null | undefined): SupportedLocale | null {
  if (!input) return null;
  const primary = input.toLowerCase().split(/[-_]/)[0];
  if (!primary) return null;
  return isSupportedLocale(primary) ? primary : null;
}
